import React from 'react';
import {
  Box,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  Assignment,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';
import type { TApiReadyCovenant, TApiReadyCovenantDocument } from '~/modules/covenants/types.ts';
import {
  MANAGER_READY_COVENANTS_EMPTY_VALUE,
} from '../../constants.ts';
import { ReadyCovenantMetaInfo } from './ReadyCovenantMetaInfo.tsx';
import { ReadyCovenantDocumentsSection } from './ReadyCovenantDocumentsSection.tsx';

interface IManagerReadyCovenantItemProps {
  covenant: TApiReadyCovenant;
  globalIndex: number;
  covenantKey: string;
  isExpanded: boolean;
  copiedCovenantKey: string | null;
  loadedDocuments: TApiReadyCovenantDocument[];
  loadedCount: number;
  totalDocumentIds: number;
  isDocumentsLoading: boolean;
  documentsErrorText?: string;
  onToggleCovenant: (index: number) => void;
  onCopyMonitoringId: (covenantKey: string, grafId: number) => void | Promise<void>;
  onLoadDocuments: (covenant: TApiReadyCovenant) => void | Promise<void>;
}

export const ReadyCovenantItem: React.FC<IManagerReadyCovenantItemProps> = ({
  covenant,
  globalIndex,
  covenantKey,
  isExpanded,
  copiedCovenantKey,
  loadedDocuments,
  loadedCount,
  totalDocumentIds,
  isDocumentsLoading,
  documentsErrorText,
  onToggleCovenant,
  onCopyMonitoringId,
  onLoadDocuments,
}) => (
  <Paper variant="outlined" sx={managerComponentStyles.readyCard}>
    <ListItem disablePadding sx={dataFormStyles.dialogListItem}>
      <ListItemButton
        onClick={() => onToggleCovenant(globalIndex)}
        sx={managerComponentStyles.readyListItemBtn}
      >
        <ListItemIcon sx={dataFormStyles.dialogListIcon}>
          <Assignment color="primary" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={managerComponentStyles.readyTitleBlock}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={managerComponentStyles.readyShortName}
              >
                {covenant.shortName || MANAGER_READY_COVENANTS_EMPTY_VALUE}
              </Typography>
              <Box sx={managerComponentStyles.readyPreviewBox}>
                <ReadyCovenantMetaInfo
                  covenant={covenant}
                  covenantKey={covenantKey}
                  copiedCovenantKey={copiedCovenantKey}
                  onCopyMonitoringId={onCopyMonitoringId}
                />
              </Box>
            </Box>
          }
        />
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
    </ListItem>

    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
      <Box sx={managerComponentStyles.readyDetails}>
        <Box sx={managerComponentStyles.readyLongNameRow}>
          <Box sx={managerComponentStyles.readyInfoIcon}>
            <InfoOutlinedIcon fontSize="small" />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={managerComponentStyles.readyLongName}
          >
            {covenant.longName || MANAGER_READY_COVENANTS_EMPTY_VALUE}
          </Typography>
        </Box>
        <ReadyCovenantDocumentsSection
          covenant={covenant}
          covenantKey={covenantKey}
          loadedDocuments={loadedDocuments}
          loadedCount={loadedCount}
          totalDocumentIds={totalDocumentIds}
          isDocumentsLoading={isDocumentsLoading}
          documentsErrorText={documentsErrorText}
          onLoadDocuments={onLoadDocuments}
        />
      </Box>
    </Collapse>
  </Paper>
);


